import type { MetaInfoStore, ListMetaInfoQuery } from './metaInfoStore';
import type { MetaDataStore } from './metaDataStore';
import type { StringMap } from '../model/types';

/**
 * Generic, reusable document store built ON TOP of {@link MetaInfoStore} +
 * {@link MetaDataStore}.
 *
 * A "document" is modeled as two halves:
 *   1. a queryable MetaInfo pointer row in DDB (primaryIdentifier + s3Key +
 *      any index attributes), and
 *   2. an opaque MetaData body in S3 (the JSON-serialized document).
 *
 * Templates, tasks, estimates — anything — are all just documents. To add a new
 * document type you instantiate a new DocumentStore<T>; no new plumbing:
 *
 *   const templates = new DocumentStore<Template>(mi, md, 'templates');
 *   await templates.save('tpl-1', myTemplate, { owner: 'jsmith' });
 *   const tpl = await templates.get('tpl-1');   // -> Template | undefined
 *   const rows = await templates.list();         // -> pointer rows
 *
 * Pure TS — no React. Calls the two stores only (never ApiClient directly).
 */

/** Attribute keys used on the MetaInfo pointer row. */
export const PRIMARY_IDENTIFIER_KEY = 'primaryIdentifier';
export const S3_KEY_ATTRIBUTE = 's3Key';
export const COLLECTION_ATTRIBUTE = 'collection';

export class DocumentStore<T> {
  constructor(
    private readonly metaInfoStore: MetaInfoStore,
    private readonly metaDataStore: MetaDataStore,
    /** Logical collection name, e.g. 'templates'. Namespaces S3 keys and rows. */
    private readonly collectionName: string,
  ) {}

  /** Deterministic S3 object key for a document id within this collection. */
  private objectKeyFor(id: string): string {
    return `${this.collectionName}/${id}.json`;
  }

  /**
   * Persists a document: uploads the JSON body to S3, then writes the queryable
   * pointer row to DDB.
   */
  async save(
    id: string,
    doc: T,
    indexAttributes: StringMap = {},
  ): Promise<void> {
    const objectKey = this.objectKeyFor(id);
    await this.metaDataStore.uploadMetaData(
      objectKey,
      JSON.stringify(doc),
      'application/json',
    );
    await this.metaInfoStore.createMetaInfo({
      [PRIMARY_IDENTIFIER_KEY]: id,
      [S3_KEY_ATTRIBUTE]: objectKey,
      [COLLECTION_ATTRIBUTE]: this.collectionName,
      ...indexAttributes,
    });
  }

  /**
   * Updates an existing document: re-uploads the JSON body, then updates the
   * pointer row's index attributes.
   */
  async update(
    id: string,
    doc: T,
    indexAttributes: StringMap = {},
  ): Promise<void> {
    const objectKey = this.objectKeyFor(id);
    await this.metaDataStore.uploadMetaData(
      objectKey,
      JSON.stringify(doc),
      'application/json',
    );
    await this.metaInfoStore.updateMetaInfo(
      { [PRIMARY_IDENTIFIER_KEY]: id },
      {
        [S3_KEY_ATTRIBUTE]: objectKey,
        [COLLECTION_ATTRIBUTE]: this.collectionName,
        ...indexAttributes,
      },
    );
  }

  /**
   * Loads a document: fetches the pointer row, then reads and parses the S3
   * body. Returns undefined if the pointer row does not exist.
   */
  async get(id: string): Promise<T | undefined> {
    const fetched = await this.metaInfoStore.fetchMetaInfo({
      [PRIMARY_IDENTIFIER_KEY]: id,
    });
    const item = fetched.item;
    if (!item) {
      return undefined;
    }
    const objectKey = item[S3_KEY_ATTRIBUTE] ?? this.objectKeyFor(id);
    const read = await this.metaDataStore.readMetaData(objectKey);
    return JSON.parse(read.content) as T;
  }

  /**
   * Lists the pointer rows for this collection (the queryable MetaInfo records,
   * NOT the S3 bodies). Callers can hydrate individual documents via {@link get}.
   */
  async list(query: ListMetaInfoQuery = {}): Promise<StringMap[]> {
    const response = await this.metaInfoStore.listMetaInfo(query);
    return response.items;
  }
}
